import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useIntl } from 'react-intl';
import { motion } from 'framer-motion';
import { Calendar, Share2, Bookmark, ChevronLeft, Clock, Eye } from 'lucide-react';
import { useParams, Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import { getNewsBySlug, getNewsById, getNewsDescriptions, formatSingleNews, formatNewsDescriptions } from '../api/strapi/newsPageApi';
import { useLanguage } from '../context';
import { getLocalizedPath } from '../helpers/routes';
import SkeletonImage from '../components/common/SkeletonImage';

const NewsDetailPage = () => {
  const intl = useIntl();
  const { slug } = useParams();
  const { language } = useLanguage();
  const [newsItem, setNewsItem] = useState(null);
  const [relatedNews, setRelatedNews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchNewsItem = async () => {
      try {
        setLoading(true);

        // Try to fetch by slug first
        let newsData = null;
        const slugResult = await getNewsBySlug(slug, language);
        if (slugResult.ok) {
          newsData = slugResult.data;
        } else {
          const numericId = parseInt(slug, 10);
          if (!Number.isNaN(numericId)) {
            const idResult = await getNewsById(numericId, language);
            if (idResult.ok) {
              newsData = idResult.data;
            }
          }
        }

        const formattedNewsItem = newsData ? formatSingleNews(newsData) : null;
        setNewsItem(formattedNewsItem);

        // Fetch related news (other news items in the same category or recent ones)
        try {
          const allNewsResult = await getNewsDescriptions(language);
          const allFormattedNews =
            allNewsResult.ok && Array.isArray(allNewsResult.data)
              ? formatNewsDescriptions(allNewsResult.data)
              : [];

          // Filter out the current news item and get related items (same category or recent)
          const filteredNews = allFormattedNews
            .filter(item => item.id !== formattedNewsItem?.id)
            .slice(0, 3); // Limit to 3 related items

          setRelatedNews(filteredNews);
        } catch (relatedError) {
          console.warn('Failed to fetch related news:', relatedError);
          // Still set an empty array so the UI renders properly
          setRelatedNews([]);
        }
      } catch (error) {
        console.error('Error fetching news item:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchNewsItem();
  }, [slug, language]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#16A34A]"></div>
      </div>
    );
  }

  if (!newsItem) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-[#0a1628] mb-4">
            {intl.formatMessage({ id: "news.detail.notFoundTitle" })}
          </h2>
          <p className="text-slate-600">
            {intl.formatMessage({ id: "news.detail.notFoundDescription" })}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>{newsItem.title} | News | Africa Systems</title>
        <meta name="description" content={newsItem.excerpt ? newsItem.excerpt.substring(0, 160) + '...' : newsItem.content.substring(0, 160) + '...'} />
      </Helmet>

      {/* Header Section */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12 py-4">
          <div className="flex items-center">
            <Link
              to={getLocalizedPath("/news", language)}
              className="flex items-center text-[#16A34A] hover:text-[#15803D] transition-colors duration-200"
            >
              <ChevronLeft className="w-5 h-5 mr-2" />
              {intl.formatMessage({ id: "back.to.news" })}
            </Link>
          </div>
        </div>
      </header>

      <article className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="bg-white border border-slate-200 rounded-xl overflow-hidden"
        >
          {/* Featured Image */}
          <div className="relative">
            <SkeletonImage
              src={newsItem.image}
              alt={newsItem.title}
              className="w-full h-96"
              imgClassName="w-full h-full object-cover"
            />
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-6">
              <div className="max-w-4xl mx-auto">
                <span className="inline-block bg-[#16A34A] text-white text-sm font-semibold px-3 py-1 rounded-full mb-4">
                  {newsItem.category}
                </span>
                <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">{newsItem.title}</h1>
                <div className="flex items-center text-white/90">
                  <Calendar className="w-5 h-5 mr-2" />
                  <span>{newsItem.date}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Article Content */}
          <div className="p-8">
            {/* Author Info */}
            <div className="flex items-center mb-8 pb-8 border-b border-slate-200">
              <SkeletonImage
                src={newsItem.author.avatar}
                alt={newsItem.author.name}
                className="w-16 h-16 rounded-full mr-4"
                imgClassName="w-full h-full rounded-full object-cover"
                icon={false}
              />
              <div>
                <h3 className="font-semibold text-[#0a1628]">{newsItem.author.name}</h3>
                <p className="text-slate-600">{newsItem.author.role}</p>
                <div className="flex items-center text-sm text-slate-500 mt-1">
                  <Clock className="w-4 h-4 mr-1" />
                  <span>{newsItem.readTime}</span>
                  <Eye className="w-4 h-4 mr-1 ml-4" />
                  <span>{newsItem.views} {intl.formatMessage({ id: "common.views" })}</span>
                </div>
              </div>
            </div>

            {/* Article Content */}
            <div className="news-content">
              <ReactMarkdown rehypePlugins={[rehypeRaw]}>{newsItem.content}</ReactMarkdown>
            </div>

            {/* Tags */}
            {newsItem.tags && Array.isArray(newsItem.tags) && newsItem.tags.length > 0 && (
              <div className="mt-8 pt-8 border-t border-slate-200">
                <h3 className="text-lg font-semibold text-[#0a1628] mb-4">
                  {intl.formatMessage({ id: "news.detail.tags" })}
                </h3>
                <div className="flex flex-wrap gap-2">
                  {newsItem.tags.map((tag, index) => (
                    <span
                      key={index}
                      className="bg-slate-100 text-slate-700 px-3 py-1 rounded-full text-sm"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Action Buttons */}
            {/* <div className="mt-8 pt-8 border-t border-gray-200 flex flex-wrap gap-4">
              <button className="flex items-center bg-green-600 hover:bg-green-700 text-white font-semibold py-2 px-6 rounded-lg transition-colors duration-200">
                <Bookmark className="w-5 h-5 mr-2" />
                Save Article
              </button>
              <button className="flex items-center bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold py-2 px-6 rounded-lg transition-colors duration-200">
                <Share2 className="w-5 h-5 mr-2" />
                Share
              </button>
            </div> */}
          </div>
        </motion.div>

        {/* Related News */}
        {relatedNews && Array.isArray(relatedNews) && relatedNews.length > 0 && (
          <section className="mt-16">
            <h2 className="text-3xl font-bold text-[#0a1628] mb-8">
              {intl.formatMessage({ id: "news.detail.related" })}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {relatedNews.map((item, index) => (
                <Link
                  to={getLocalizedPath(`/news/${item.slug || item.id}`, language)}
                  key={item.id}
                  className="block"
                >
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    className="group bg-white border border-slate-200 hover:border-[#16A34A]/40 hover:shadow-lg transition-all duration-300 rounded-xl overflow-hidden"
                  >
                    <SkeletonImage
                      src={item.image}
                      alt={item.title}
                      className="w-full h-48"
                      imgClassName="w-full h-full object-cover"
                    />
                    <div className="p-6">
                      <h3 className="text-xl font-bold text-[#0a1628] mb-3 line-clamp-2 group-hover:text-[#16A34A] transition-colors">{item.title}</h3>
                      <div className="flex items-center text-sm text-slate-500">
                        <Calendar className="w-4 h-4 mr-1" />
                        <span>{item.date}</span>
                      </div>
                    </div>
                  </motion.div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </article>
    </div>
  );
};

export default NewsDetailPage;
